import { useEffect, useMemo, useState } from 'react'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import moment from 'moment'
import { useReceiveScanLogStore } from 'Store/ReceiveScanLog'

const PAGE_SIZE = 20

export type SortDirection = 'asc' | 'desc'
export type SortableField = 'createdat' | 'projectid' | 'status' | 'inventorycode' | 'id'

const STATUS_OPTIONS = ['success', 'failed']

const ViewModel = () => {
  const { data, totalCount, isLoading, getAll, clearState } = useReceiveScanLogStore(
    state => ({
      data: state.data,
      totalCount: state.totalCount,
      isLoading: state.loading,
      getAll: state.getAll,
      clearState: state.clearState,
    }),
    shallow
  )

  const [page, setPage] = useState(1)
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [isError, setIsError] = useState(false)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [projectId, setProjectId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [sortField, setSortField] = useState<SortableField>('createdat')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const formatDate = (value: string | null) => {
    return value ? moment(value).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss') : '-'
  }

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  }, [totalCount])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setIsError(false)
    setExpandedIds([])
    getAll({
      page,
      pageSize: PAGE_SIZE,
      sortBy: sortField,
      order: sortDirection,
      status: statusFilter || undefined,
      inventoryCode: debouncedSearch || undefined,
      projectId: projectId ? Number(projectId) : undefined,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    }).then(({ success, data: resData }) => {
      if (!success) {
        setIsError(true)
        toast.error('เกิดข้อผิดพลาดในการดึงข้อมูล' + (resData ? ' : ' + resData : ''))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortField, sortDirection, statusFilter, debouncedSearch, projectId, dateFrom, dateTo])

  useEffect(() => {
    return () => {
      clearState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const isExpanded = (id: number) => expandedIds.includes(id)

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  const onSort = (field: SortableField) => {
    setPage(1)
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const onSearchChange = (value: string) => {
    setSearch(value)
  }

  const onStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const onProjectIdChange = (value: string) => {
    setProjectId(value.replace(/[^0-9]/g, ''))
    setPage(1)
  }

  const onDateRangeChange = (from: string, to: string) => {
    setDateFrom(from)
    setDateTo(to)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setDebouncedSearch('')
    setStatusFilter('')
    setProjectId('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return {
    data,
    totalCount,
    isLoading,
    isError,
    page,
    pageSize: PAGE_SIZE,
    search,
    onSearchChange,
    statusFilter,
    statusOptions: STATUS_OPTIONS,
    onStatusFilterChange,
    projectId,
    onProjectIdChange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    clearFilters,
    isExpanded,
    sortField,
    sortDirection,
    onSort,
    toggleExpand,
    goToPage,
    formatDate,
  }
}

export default ViewModel
